import { ICommonProcedure } from '@dataTypes/appointment.type'
import { BundledProcedure } from '@dataTypes/bundledProcedure.type'
import { ProcedureSettingsType } from '@dataTypes/procedureSettings.type'
import { IProcedureDentalCondition } from '@dataTypes/treatmentPlan.types'

export const normalizeSearchQuery = (query: string): string =>
  query.trim().toLowerCase()

const normalizeSearchText = (value: string | null | undefined): string =>
  value?.toLowerCase() ?? ''

export const formatDentalConditionNames = (
  conditions: IProcedureDentalCondition[] | undefined,
): string => {
  const seen = new Set<string>()
  const names: string[] = []
  for (const mapping of conditions ?? []) {
    const name = mapping.dental_condition?.name?.trim()
    if (!name) {
      continue
    }
    const key = name.toLowerCase()
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    names.push(name)
  }
  return names.join(', ')
}

export const formatBundleDentalConditionNames = (
  bundle: BundledProcedure,
): string => {
  const conditions = (bundle.bundled_procedure_items ?? []).flatMap(
    (item) => item.procedure?.procedure_dental_conditions ?? [],
  )
  return formatDentalConditionNames(conditions)
}

const dentalConditionMatchesQuery = (
  conditions: IProcedureDentalCondition[] | undefined,
  normalizedQuery: string,
): boolean =>
  (conditions ?? []).some((mapping) => {
    const condition = mapping.dental_condition
    const searchableFields = [
      mapping.dental_condition_id,
      condition?.slug,
      condition?.name,
      condition?.category,
    ]
    return searchableFields.some((field) =>
      normalizeSearchText(field).includes(normalizedQuery),
    )
  })

const procedureSettingMatchesQuery = (
  setting: ProcedureSettingsType,
  normalizedQuery: string,
): boolean => {
  const code = normalizeSearchText(setting.procedure?.procedure_code)
  const description = normalizeSearchText(setting.procedure?.description)
  const settingDescription = normalizeSearchText(setting.description)
  return (
    code.includes(normalizedQuery) ||
    description.includes(normalizedQuery) ||
    settingDescription.includes(normalizedQuery) ||
    dentalConditionMatchesQuery(
      setting.procedure?.procedure_dental_conditions,
      normalizedQuery,
    )
  )
}

export const bundleMatchesQuery = (
  bundle: BundledProcedure,
  normalizedQuery: string,
): boolean => {
  if (!normalizedQuery) {
    return false
  }
  const bundledCode = bundle.bundled_code?.toLowerCase() ?? ''
  const bundledDescription = bundle.bundled_name?.toLowerCase() ?? ''
  if (
    bundledCode.includes(normalizedQuery) ||
    bundledDescription.includes(normalizedQuery)
  ) {
    return true
  }
  return (bundle.bundled_procedure_items ?? []).some((item) => {
    const code = normalizeSearchText(item.procedure?.procedure_code)
    const description = normalizeSearchText(item.procedure?.description)
    return (
      code.includes(normalizedQuery) ||
      description.includes(normalizedQuery) ||
      dentalConditionMatchesQuery(
        item.procedure?.procedure_dental_conditions,
        normalizedQuery,
      )
    )
  })
}

export const filterVisibleBundledProcedures = (
  bundles: BundledProcedure[],
): BundledProcedure[] => bundles.filter((bundle) => bundle.is_hidden !== true)

export const filterVisibleCommonProcedures = (
  procedures: ICommonProcedure[],
): ICommonProcedure[] =>
  procedures
    .filter((procedure) => procedure.is_hidden !== true)
    .sort((left, right) => (left.position ?? 999) - (right.position ?? 999))

export const resolveCommonProcedureToProcedureSettings = (
  commonProcedure: ICommonProcedure,
  procedureSettings: ProcedureSettingsType[],
): ProcedureSettingsType | null => {
  const setting = procedureSettings.find(
    (item) =>
      (item.procedure?.procedure_id ?? item.procedure_id) ===
      commonProcedure.procedure_id,
  )
  return setting ?? null
}

export const filterProcedureSettingsForSearch = (
  settings: ProcedureSettingsType[],
  query: string,
): ProcedureSettingsType[] => {
  const normalizedQuery = normalizeSearchQuery(query)
  if (!normalizedQuery) {
    return []
  }
  return settings.filter((setting) =>
    procedureSettingMatchesQuery(setting, normalizedQuery),
  )
}

export const filterBundledProceduresForSearch = (
  bundles: BundledProcedure[],
  query: string,
): BundledProcedure[] => {
  const normalizedQuery = normalizeSearchQuery(query)
  if (!normalizedQuery) {
    return []
  }
  return filterVisibleBundledProcedures(bundles).filter((bundle) =>
    bundleMatchesQuery(bundle, normalizedQuery),
  )
}

export const resolveBundleToProcedureSettings = (
  bundle: BundledProcedure,
  procedureSettings: ProcedureSettingsType[],
): ProcedureSettingsType[] => {
  const settingsByProcedureId = new Map(
    procedureSettings.map((setting) => [
      setting.procedure?.procedure_id ?? setting.procedure_id,
      setting,
    ]),
  )
  const resolved: ProcedureSettingsType[] = []
  for (const item of bundle.bundled_procedure_items ?? []) {
    const setting = settingsByProcedureId.get(item.procedure_id)
    if (setting) {
      resolved.push(setting)
    }
  }
  return resolved
}

export const formatBundleSearchLabel = (bundle: BundledProcedure): string => {
  const childCodes = (bundle.bundled_procedure_items ?? [])
    .map((item) => item.procedure?.procedure_code)
    .filter((code): code is string => Boolean(code))
  const count = childCodes.length
  const codeSummary =
    childCodes.length > 0 ? childCodes.join(', ') : 'no procedures'
  const description = bundle.bundled_name?.trim()
  const title = description
    ? `${bundle.bundled_code} — ${description}`
    : bundle.bundled_code
  return `${title} (${count} procedure${count === 1 ? '' : 's'}: ${codeSummary})`
}

export type TxPlanProcedureSearchResults = {
  procedures: ProcedureSettingsType[]
  bundles: BundledProcedure[]
}

export const computeTxPlanProcedureSearchResults = (
  procedureSettings: ProcedureSettingsType[],
  bundledProcedures: BundledProcedure[],
  query: string,
): TxPlanProcedureSearchResults => ({
  procedures: filterProcedureSettingsForSearch(procedureSettings, query),
  bundles: filterBundledProceduresForSearch(bundledProcedures, query),
})
